import {
  ExpandableSection,
  Spacing,
  Typography,
  theme as defaultTheme,
} from "@mindlercare/ui-web";
import { ReactNode } from "react";
import { styled } from "styled-components";

import { IcbtExerciseLabel } from "../ContentfulRichText/IcbtExerciseLabel";
import { IcbtPage } from "../IcbtPage";
import { useIcbtTranslation } from "../translations";
import type { ContentfulEntryLink, PageWithContent } from "../types";

export type IcbtQuestionnairesProps = {
  pages: PageWithContent[];
  renderQuestionnaire: (
    entry: ContentfulEntryLink,
    opts?: { variant?: "answers" | "full" },
  ) => ReactNode;
  renderPatientNote?: (entry: ContentfulEntryLink) => ReactNode;
  enableAudio?: boolean;
  enableVideo?: boolean;
};

export const IcbtQuestionnaires = ({
  pages,
  renderQuestionnaire,
  renderPatientNote,
  enableAudio,
  enableVideo,
}: IcbtQuestionnairesProps) => {
  return (
    <div>
      {pages.map((page, i) => (
        <IcbtQuestionnaire
          key={page.id}
          page={page}
          pageNumber={i + 1}
          totalPages={pages.length}
          renderQuestionnaire={renderQuestionnaire}
          renderPatientNote={renderPatientNote}
          enableAudio={enableAudio}
          enableVideo={enableVideo}
        />
      ))}
    </div>
  );
};

type IcbtQuestionnaireProps = {
  page: PageWithContent;
  pageNumber: number;
  totalPages: number;
  renderQuestionnaire: (
    entry: ContentfulEntryLink,
    opts?: { variant?: "answers" | "full" },
  ) => ReactNode;
  renderPatientNote?: (entry: ContentfulEntryLink) => ReactNode;
  enableAudio?: boolean;
  enableVideo?: boolean;
};

const IcbtQuestionnaire = ({
  page,
  pageNumber,
  totalPages,
  renderQuestionnaire,
  renderPatientNote,
  enableAudio,
  enableVideo,
}: IcbtQuestionnaireProps) => {
  const t = useIcbtTranslation();

  return (
    <div>
      <Typography insensitive variant="h4">
        {page.title}
      </Typography>
      <Spacing b={8} />
      {page.content.links.entries.block.map((entry) => {
        if (entry.__typename === "IcbtExerciseLabel") {
          return (
            <div key={entry.sys.id}>
              <IcbtExerciseLabel {...entry} />
              <Spacing b={8} />
            </div>
          );
        }
        if (entry.__typename === "Questionnaire") {
          return (
            <div key={entry.sys.id}>
              {renderQuestionnaire(entry, { variant: "answers" })}
            </div>
          );
        }
        if (entry.__typename === "PatientIcbtNote" && renderPatientNote) {
          return <div key={entry.sys.id}>{renderPatientNote(entry)}</div>;
        }
        return null;
      })}
      <ExpandableSection>
        <StyledToggle>
          <Typography variant="h5" insensitive>
            {t.VIEW_ICBT_PAGE}
          </Typography>
        </StyledToggle>
        <ExpandableSection.Content>
          <Spacing l={4} t={8} />
          <PageWrapper>
            <IcbtPage
              page={page}
              renderQuestionnaire={renderQuestionnaire}
              renderPatientNote={renderPatientNote}
              enableAudio={enableAudio}
              enableVideo={enableVideo}
            />
            <PageNumber>
              {pageNumber} {t.OF} {totalPages}
            </PageNumber>
          </PageWrapper>
        </ExpandableSection.Content>
      </ExpandableSection>
    </div>
  );
};

const PageWrapper = styled.div`
  border-left: 0.5rem solid ${defaultTheme.COLORS.GRAY_LIGHT};
  margin-left: 1rem;
  padding-left: 0.5rem;
`;

const StyledToggle = styled(ExpandableSection.Toggle)`
  border-bottom: none;
  &:hover {
    cursor: pointer;
    background-color: white;
  }
`;

const PageNumber = styled.span`
  display: flex;
  justify-content: center;
  color: ${defaultTheme.COLORS.GRAY};
  padding-bottom: ${defaultTheme.PADDING.LG};
`;
